import React, { useState } from 'react';
import { Card, Button, Typography, Table, Empty, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import OntologyObjectQueryModal from './OntologyObjectQueryModal';
import { MockObjectItem, ObjectMappingInfo, MappingAttributeRow } from '@/types/semanticDataMap';

const { Title, Paragraph, Text } = Typography;

interface MappingInfoProps {
  selectedObjectId: string;
  mappingInfo: ObjectMappingInfo | null;
  ontologyId: number;
  selectedObject: MockObjectItem | null;
  isLoading?: boolean;
}

const MappingInfo: React.FC<MappingInfoProps> = ({
  selectedObjectId,
  mappingInfo,
  ontologyId,
  selectedObject,
  isLoading,
}) => {
  const [queryModalVisible, setQueryModalVisible] = useState(false);

  const mappingColumns = [
    {
      title: '与主对象关系',
      dataIndex: 'attributeName',
      key: 'attributeName',
      className: 'text-sm',
    },
    {
      title: '展示关系',
      dataIndex: 'relationToObject',
      key: 'relationToObject',
      className: 'text-sm',
      render: (val: string) => val === 'DIRECT' ? '展示关系' : '父对象继承',
    },
    {
      title: '映射字段',
      dataIndex: 'mappedColumnNames',
      key: 'mappedColumnNames',
      className: 'text-sm',
      render: (names: string[]) => names?.join(', ') || '--',
    },
    {
      title: '映射表',
      dataIndex: 'mappedTableNames',
      key: 'mappedTableNames',
      className: 'text-sm',
      render: (names: string[]) => names?.join(', ') || '--',
    },
    {
      title: '映射系统',
      dataIndex: 'mappedSystemNames',
      key: 'mappedSystemNames',
      className: 'text-sm',
      render: (names: string[]) => names?.join(', ') || '--',
    },
  ];

  const unmappedText = mappingInfo?.unmappedAttributes?.length
    ? mappingInfo.unmappedAttributes.join('、')
    : '无';

  return (
    <Card className="border border-gray-200 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <Title level={5} className="text-gray-700 font-medium">映射信息</Title>
        {mappingInfo && (
          <div className="flex gap-2">
            <Button
              type="default"
              icon={<SearchOutlined />}
              onClick={() => setQueryModalVisible(true)}
              size="small"
            >
              查询数据
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6"><Spin /></div>
      ) : mappingInfo ? (
        <>
          <div className="mb-6">
            <Title level={5} className="text-gray-700 font-medium mb-2">对象映射概览</Title>
            <Paragraph className="text-sm text-gray-700 mb-0">
              共关联 {mappingInfo.systemCount} 个系统，{mappingInfo.tableCount} 张表，
              {mappingInfo.fieldCount} 个映射字段。<br />
              未被映射的属性为：{unmappedText}。
            </Paragraph>
          </div>

          <div>
            <Title level={5} className="text-gray-700 font-medium mb-3">对象映射详情</Title>

            <div className="mt-4">
              <div className="bg-gray-50 px-4 py-1.5 border-l-4 border-blue-500 mb-3">
                <Text strong className="text-sm">{selectedObject?.name}</Text>
              </div>
              <Table
                size="small"
                pagination={false}
                dataSource={mappingInfo.rows}
                columns={mappingColumns}
                rowKey={(record: MappingAttributeRow) => `${record.attributeId}`}
                className="border border-gray-100"
                style={{ fontSize: '13px' }}
              />
            </div>
          </div>
        </>
      ) : (
        <Empty description={'暂无数据'} />
      )}

      <OntologyObjectQueryModal
        visible={queryModalVisible}
        onCancel={() => setQueryModalVisible(false)}
        ontologyId={ontologyId}
        conceptName={selectedObject?.name || ''}
        attributes={selectedObject?.attributes?.map(attr => attr.name) || []}
      />
    </Card>
  );
};

export default MappingInfo;
